/**
 * Act1ChatArea — 第一幕聊天区域
 *
 * 多人对话展示，支持亲友/主角/系统消息
 */
import React, { useRef, useEffect } from 'react';
import { Zap } from 'lucide-react';
import type { ChatMessage, SenderRole } from '@shared/types/gameState';
import type { Character } from '@shared/types/character';
import type { FamilyMember } from '@shared/types/family';

interface Act1ChatAreaProps {
  messages: ChatMessage[];
  protagonistA: Character;
  protagonistB: Character;
  familyA: FamilyMember[];
  familyB: FamilyMember[];
  isTyping: boolean;
  typingSpeaker: string | null;
  /** 流式文本（正在生成中的消息） */
  streamingText?: string;
  streamingAgentName?: string | null;
  streamingAgentRole?: SenderRole | null;
  currentSpeaker?: string | null;
  /** 卡牌叙事流式状态 */
  isCardNarrating?: boolean;
  cardNarrativeText?: string;
}

export const Act1ChatArea: React.FC<Act1ChatAreaProps> = React.memo(({
  messages,
  protagonistA,
  protagonistB,
  familyA,
  familyB,
  isTyping,
  typingSpeaker,
  streamingText,
  streamingAgentName,
  streamingAgentRole,
  currentSpeaker,
  isCardNarrating,
  cardNarrativeText,
}) => {
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, streamingText, cardNarrativeText]);

  // Build a lookup for all participants
  const participants = React.useMemo(() => {
    const map: Record<string, { name: string; role: string; avatar?: string; color: string }> = {};
    map[protagonistA.id] = { name: protagonistA.name, role: 'protagonistA', avatar: protagonistA.avatar, color: 'bg-coral-500' };
    map[protagonistB.id] = { name: protagonistB.name, role: 'protagonistB', avatar: protagonistB.avatar, color: 'bg-purple-500' };
    familyA.forEach(f => { map[f.id] = { name: f.name, role: 'familyA', color: 'bg-amber-500' }; });
    familyB.forEach(f => { map[f.id] = { name: f.name, role: 'familyB', color: 'bg-teal-500' }; });
    return map;
  }, [protagonistA, protagonistB, familyA, familyB]);

  const renderMessage = (msg: ChatMessage) => {
    if (msg.type === 'system') {
      return (
        <div key={msg.id} className="flex justify-center my-2">
          <span className="text-[10px] text-[#9CA3AF] bg-[#F5EDE3] px-3 py-1 rounded-full">
            {msg.text}
          </span>
        </div>
      );
    }

    if (msg.type === 'narrative') {
      return (
        <div key={msg.id} className="flex justify-center my-3">
          <div className="bg-gradient-to-r from-[#F5EDE3] to-[#EDE8F5] px-4 py-2 rounded-xl max-w-[85%]">
            <p className="text-xs text-[#6B5B4F] italic text-center">{msg.text}</p>
          </div>
        </div>
      );
    }

    if (msg.type === 'thought') {
      return (
        <div key={msg.id} className="flex justify-center my-2">
          <span className="text-[10px] text-[#9B8EC4] italic">
            💭 {msg.text}
          </span>
        </div>
      );
    }

    // Text bubble — multi-person layout
    const p = participants[msg.senderId];
    const isProtagonistA = msg.senderRole === 'protagonistA';
    const isRight = isProtagonistA; // Male protagonist on right

    return (
      <div key={msg.id} className={`flex items-end gap-1.5 mb-2 ${isRight ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Avatar */}
        <div className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-white text-[10px] font-bold ${p?.color || 'bg-gray-400'} overflow-hidden`}>
          {p?.avatar
            ? <img src={p.avatar} className="w-full h-full object-cover" alt={p?.name} />
            : (p?.name?.[0] || '?')
          }
        </div>

        {/* Name + bubble */}
        <div className={`flex flex-col ${isRight ? 'items-end' : 'items-start'} max-w-[70%]`}>
          <span className="text-[9px] text-[#9CA3AF] mb-0.5 px-1">{msg.senderName}</span>
          <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
            isProtagonistA
              ? 'bg-coral-500 text-white rounded-br-md'
              : msg.senderRole === 'protagonistB'
                ? 'bg-purple-500 text-white rounded-bl-md'
                : msg.senderRole === 'familyA'
                  ? 'bg-amber-100 text-[#6B5B4F] rounded-bl-md'
                  : 'bg-teal-100 text-[#6B5B4F] rounded-bl-md'
          }`}>
            {msg.text}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 md:px-8 no-scrollbar">
      <div className="max-w-2xl mx-auto">
        {messages.map(renderMessage)}

        {/* 卡牌叙事流式气泡 */}
        {isCardNarrating && cardNarrativeText && (
          <div className="flex justify-center my-3">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-2 rounded-xl max-w-[85%] border border-amber-200">
              <p className="text-xs text-[#6B5B4F] italic text-center">
                🎬 {cardNarrativeText}
                <span className="inline-block w-0.5 h-3 bg-current ml-0.5 animate-pulse" />
              </p>
            </div>
          </div>
        )}

        {/* 流式消息气泡 — 正在生成中的 NPC 发言 */}
        {streamingText && streamingAgentName && currentSpeaker && (() => {
          const p = participants[currentSpeaker];
          const isProtagonistA = streamingAgentRole === 'protagonistA';
          const isRight = isProtagonistA;
          return (
            <div className={`flex items-end gap-1.5 mb-2 ${isRight ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-white text-[10px] font-bold ${p?.color || 'bg-gray-400'} overflow-hidden`}>
                {p?.avatar
                  ? <img src={p.avatar} className="w-full h-full object-cover" alt={p?.name} />
                  : (p?.name?.[0] || streamingAgentName[0] || '?')
                }
              </div>
              <div className={`flex flex-col ${isRight ? 'items-end' : 'items-start'} max-w-[70%]`}>
                <span className="text-[9px] text-[#9CA3AF] mb-0.5 px-1">{streamingAgentName}</span>
                <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                  isProtagonistA
                    ? 'bg-coral-500 text-white rounded-br-md'
                    : streamingAgentRole === 'protagonistB'
                      ? 'bg-purple-500 text-white rounded-bl-md'
                      : streamingAgentRole === 'familyA'
                        ? 'bg-amber-100 text-[#6B5B4F] rounded-bl-md'
                        : 'bg-teal-100 text-[#6B5B4F] rounded-bl-md'
                }`}>
                  {streamingText}
                  <span className="inline-block w-0.5 h-3.5 bg-current ml-0.5 animate-pulse" />
                </div>
              </div>
            </div>
          );
        })()}

        {/* 等待下一个 NPC 发言的打字指示器（无流式文本时显示） */}
        {isTyping && typingSpeaker && !streamingText && (
          <div className="flex items-center gap-2 my-2">
            <div className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-white text-[10px] font-bold ${participants[typingSpeaker]?.color || 'bg-gray-400'}`}>
              {participants[typingSpeaker]?.name?.[0] || '?'}
            </div>
            <div className="bg-[#F5EDE3] px-3 py-2 rounded-2xl rounded-bl-md">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-[#9CA3AF] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>
    </div>
  );
});

Act1ChatArea.displayName = 'Act1ChatArea';
